import { toISOWithOffset } from "../utils";
import { serverNow } from "../serverClock";
import type {
  ExamSessionState,
  ExamSessionActions,
  ExamSessionConfig,
  PlatformAdapter,
  QuestionAnswer,
  QuestionStatus,
  QuestionVisitEvent,
  SaveAnswerPayload,
  ServerQuestionAnswer,
} from "./types";

export type ExamSessionStore = ExamSessionState & ExamSessionActions;

export function getUnsyncedPayloads(
  events: QuestionVisitEvent[],
): SaveAnswerPayload[] {
  if (events.length === 0) return [];
  return events
    .filter((event) => event.endTime !== null)
    .map((event) => ({
      question_id: event.questionId,
      student_answer: event.studentAnswer,
      attempt_start_time: toISOWithOffset(event.startTime),
      attempt_end_time: toISOWithOffset(event.endTime),
      mark_for_review: event.markedForReview,
    }));
}

const STORAGE_KEY_PREFIX = "exam-session";

const initialState: ExamSessionState = {
  examId: "",
  studentId: "",
  questionPaperId: "",
  answers: {},
  totalQuestions: 0,
  examStartTime: null,
  pendingEvents: [],
  currentQuestionId: null,
  currentVisitStart: null,
};

function hasAnswer(answer: QuestionAnswer): boolean {
  return (
    answer.submittedAnswers !== null &&
    Object.keys(answer.submittedAnswers).length > 0
  );
}

function computeStatus(hasAnswer: boolean, isMarked: boolean): QuestionStatus {
  if (hasAnswer && isMarked) return "answeredMarkedForReview";
  if (hasAnswer) return "answered";
  if (isMarked) return "markedForReview";
  return "notAnswered";
}

export function isMarkedStatus(status?: QuestionStatus): boolean {
  return status === "markedForReview" || status === "answeredMarkedForReview";
}

export function toServerAnswer(a: {
  questionId: string;
  studentAnswer: Record<string, unknown> | null;
  markForReview: boolean;
}): ServerQuestionAnswer {
  return {
    questionId: a.questionId,
    submittedAnswers: a.studentAnswer,
    status: a.markForReview
      ? "markedForReview"
      : a.studentAnswer
        ? "answered"
        : "notAnswered",
  };
}

function ensureAnswer(questionId: string): QuestionAnswer {
  return { questionId, submittedAnswers: null, status: "notAnswered" };
}

export function createExamSessionStore(adapter: PlatformAdapter) {
  let state: ExamSessionStore;
  const listeners = new Set<() => void>();
  let persistTimer: ReturnType<typeof setTimeout> | null = null;
  let cleared = false;

  function notify() {
    for (const l of listeners) l();
  }

  function setAndPersist(partial: Partial<ExamSessionState>) {
    state = { ...state, ...partial };
    schedulePersist();
    notify();
  }

  function schedulePersist() {
    if (cleared) return;
    if (persistTimer) clearTimeout(persistTimer);
    persistTimer = setTimeout(persist, 500);
  }

  function storageKey(examId?: string): string {
    const id = examId || state.examId;
    return id ? `${STORAGE_KEY_PREFIX}:${id}` : STORAGE_KEY_PREFIX;
  }

  function persist() {
    if (cleared) return;
    const {
      examId,
      studentId,
      questionPaperId,
      examStartTime,
      answers,
      pendingEvents,
      totalQuestions,
      submitting,
      submissionReason,
      submittedAt,
      examEndTime,
    } = state;
    adapter
      .setItem(
        storageKey(),
        JSON.stringify({
          examId,
          studentId,
          questionPaperId,
          examStartTime,
          answers,
          pendingEvents,
          totalQuestions,
          submitting,
          submissionReason,
          submittedAt,
          examEndTime,
        }),
      )
      .catch((err) => {
        console.error("[ExamSession] Failed to persist:", err);
      });
  }

  const actions: ExamSessionActions = {
    initSession(config: ExamSessionConfig) {
      const answers: Record<string, QuestionAnswer> = {};

      if (config.existingAnswers) {
        for (const sa of config.existingAnswers) {
          const isAnswered =
            sa.submittedAnswers !== null &&
            Object.keys(sa.submittedAnswers).length > 0;
          answers[sa.questionId] = {
            questionId: sa.questionId,
            submittedAnswers: sa.submittedAnswers,
            status:
              (sa.status as QuestionStatus) ||
              (isAnswered ? "answered" : "notAnswered"),
          };
        }
      }

      setAndPersist({
        examId: config.examId,
        studentId: config.studentId,
        questionPaperId: config.questionPaperId,
        examStartTime:
          config.examStartTime ?? new Date(serverNow()).toISOString(),
        examEndTime: config.examEndTime ?? state.examEndTime,
        answers,
        pendingEvents: [],
        totalQuestions: config.totalQuestions,
        currentQuestionId: null,
        currentVisitStart: null,
      });
    },

    visitQuestion(questionId: string) {
      const existing = state.answers[questionId];
      const answer = existing ?? ensureAnswer(questionId);

      if (answer.status === "notVisited") {
        answer.status = "notAnswered";
      }

      setAndPersist({
        currentQuestionId: questionId,
        currentVisitStart: new Date(serverNow()).toISOString(),
        answers: { ...state.answers, [questionId]: answer },
      });
    },

    leaveQuestion(questionId: string) {
      if (!state.currentVisitStart || state.currentQuestionId !== questionId) {
        return;
      }

      const answer = state.answers[questionId];
      const event: QuestionVisitEvent = {
        questionId,
        startTime: state.currentVisitStart,
        endTime: new Date(serverNow()).toISOString(),
        studentAnswer: answer?.submittedAnswers ?? null,
        markedForReview: isMarkedStatus(answer?.status),
      };

      setAndPersist({
        pendingEvents: [...state.pendingEvents, event],
        currentQuestionId: null,
        currentVisitStart: null,
      });
    },

    transitionQuestion(fromId: string, toId: string) {
      const now = new Date(serverNow()).toISOString();

      const updates: Partial<ExamSessionState> = {};

      if (state.currentVisitStart && state.currentQuestionId === fromId) {
        const answer = state.answers[fromId];
        const event: QuestionVisitEvent = {
          questionId: fromId,
          startTime: state.currentVisitStart,
          endTime: now,
          studentAnswer: answer?.submittedAnswers ?? null,
          markedForReview: isMarkedStatus(answer?.status),
        };
        updates.pendingEvents = [...state.pendingEvents, event];
      }

      const existing = state.answers[toId];
      const toAnswer = existing ?? ensureAnswer(toId);
      updates.currentQuestionId = toId;
      updates.currentVisitStart = now;
      updates.answers = {
        ...state.answers,
        [toId]: toAnswer,
      };

      setAndPersist(updates);
    },

    saveAnswer(
      questionId: string,
      submittedAnswers?: Record<string, unknown> | null,
    ) {
      const answer = state.answers[questionId] ?? ensureAnswer(questionId);
      const newAnswers = submittedAnswers ?? answer.submittedAnswers;
      const isAnswered =
        newAnswers !== null &&
        newAnswers !== undefined &&
        Object.keys(newAnswers).length > 0;

      setAndPersist({
        answers: {
          ...state.answers,
          [questionId]: {
            ...answer,
            submittedAnswers: newAnswers ?? answer.submittedAnswers,
            status: computeStatus(isAnswered, false),
          },
        },
      });
    },

    clearAnswer(questionId: string) {
      const answer = state.answers[questionId];
      if (!answer) return;
      const wasMarked = isMarkedStatus(answer.status);

      setAndPersist({
        answers: {
          ...state.answers,
          [questionId]: {
            ...answer,
            submittedAnswers: null,
            status: computeStatus(false, wasMarked),
          },
        },
      });
    },

    markForReview(questionId: string) {
      const answer = state.answers[questionId] ?? ensureAnswer(questionId);

      setAndPersist({
        answers: {
          ...state.answers,
          [questionId]: {
            ...answer,
            status: computeStatus(hasAnswer(answer), true),
          },
        },
      });
    },

    markSynced(syncedCount: number) {
      let removed = 0;
      const remaining = state.pendingEvents.filter((e) => {
        if (removed < syncedCount && e.endTime !== null) {
          removed++;
          return false;
        }
        return true;
      });
      setAndPersist({ pendingEvents: remaining });
    },

    markSyncedQuestionIds(questionIds: string[]) {
      const idsToRemove = new Set(questionIds);
      const remaining = state.pendingEvents.filter(
        (e) => !idsToRemove.has(e.questionId),
      );
      setAndPersist({ pendingEvents: remaining });
    },

    markSubmitting(reason, submittedAt, examEndTime) {
      setAndPersist({
        submitting: true,
        submissionReason: reason,
        submittedAt,
        examEndTime,
      });
    },

    clearSubmitting() {
      setAndPersist({
        submitting: false,
        submissionReason: undefined,
        submittedAt: undefined,
        examEndTime: undefined,
      });
    },

    hydrateFromServer(serverAnswers: ServerQuestionAnswer[]) {
      const updated = { ...state.answers };

      const unsyncedIds = new Set(state.pendingEvents.map((e) => e.questionId));

      for (const sa of serverAnswers) {
        if (unsyncedIds.has(sa.questionId)) continue;

        const isAnswered =
          sa.submittedAnswers !== null &&
          Object.keys(sa.submittedAnswers ?? {}).length > 0;
        updated[sa.questionId] = {
          questionId: sa.questionId,
          submittedAnswers: sa.submittedAnswers,
          status:
            (sa.status as QuestionStatus) ||
            (isAnswered ? "answered" : "notAnswered"),
        };
      }
      setAndPersist({ answers: updated });
    },
  };

  state = { ...initialState, ...actions };

  return {
    getState: () => state,
    flushPersist() {
      if (cleared) return;
      if (persistTimer) {
        clearTimeout(persistTimer);
        persistTimer = null;
      }
      persist();
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async rehydrate(expectedExamId?: string) {
      const raw = await adapter.getItem(storageKey(expectedExamId));
      if (!raw) return;
      try {
        const persisted = JSON.parse(raw) as Partial<ExamSessionState>;
        if (
          expectedExamId &&
          persisted.examId &&
          persisted.examId !== expectedExamId
        ) {
          return;
        }
        state = {
          ...state,
          examId: persisted.examId ?? state.examId,
          studentId: persisted.studentId ?? state.studentId,
          questionPaperId: persisted.questionPaperId ?? state.questionPaperId,
          examStartTime: persisted.examStartTime ?? state.examStartTime,
          answers: persisted.answers ?? state.answers,
          pendingEvents: persisted.pendingEvents ?? state.pendingEvents,
          totalQuestions: persisted.totalQuestions ?? state.totalQuestions,
          submitting: persisted.submitting,
          submissionReason: persisted.submissionReason,
          submittedAt: persisted.submittedAt,
          examEndTime: persisted.examEndTime,
        };
        notify();
      } catch {
        // corrupted storage — ignore
      }
    },
    async clearStorage() {
      cleared = true;
      if (persistTimer) {
        clearTimeout(persistTimer);
        persistTimer = null;
      }
      await adapter.removeItem(storageKey()).catch(() => {});
    },
  };
}
